import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ANIMATED_FLAG = 'pageAnimationsInit';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const makeKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const toArray = (input) => {
  if (!input) {
    return [];
  }

  return Array.isArray(input) ? input.filter(Boolean) : [input];
};

const setWillChange = (elements, value = 'transform, opacity') => {
  toArray(elements).forEach((element) => {
    if (element && element.style) {
      element.style.willChange = value;
    }
  });
};

const clearWillChange = (elements) => {
  setWillChange(elements, 'auto');
};

const revealSection = (selector, options = {}) => {
  const sections = document.querySelectorAll(selector);
  const sectionFlag = `pageSectionAnimated${makeKey(selector)}`;

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset[sectionFlag] === 'true') {
      return;
    }

    const fromVars = {
      y: 36,
      opacity: 0,
      ...options.from,
    };

    const toVars = {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: 'power3.out',
      ...options.to,
      scrollTrigger: {
        trigger: section,
        start: 'top 82%',
        once: true,
        markers: false,
        ...(options.scrollTrigger || {}),
      },
      onStart: () => setWillChange(section),
      onComplete: () => {
        clearWillChange(section);
        section.dataset[sectionFlag] = 'true';
        if (typeof options.onComplete === 'function') {
          options.onComplete(section);
        }
      },
    };

    gsap.fromTo(section, fromVars, toVars);
  });
};

const revealChildren = (sectionSelector, childSelector, options = {}) => {
  const sections = document.querySelectorAll(sectionSelector);
  const sectionFlag = `pageChildrenAnimated${makeKey(sectionSelector)}${makeKey(childSelector)}`;

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset[sectionFlag] === 'true') {
      return;
    }

    const children = section.querySelectorAll(childSelector);

    if (!children.length) {
      return;
    }

    const animationTarget = options.targetResolver ? options.targetResolver(children) : children;
    const targets = toArray(animationTarget);

    if (!targets.length) {
      return;
    }

    gsap.fromTo(
      targets,
      {
        y: 36,
        opacity: 0,
        scale: 0.985,
        ...options.from,
      },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.95,
        ease: 'power3.out',
        stagger: 0.08,
        ...options.to,
        scrollTrigger: {
          trigger: section,
          start: 'top 82%',
          once: true,
          markers: false,
          ...(options.scrollTrigger || {}),
        },
        onStart: () => setWillChange(targets),
      onComplete: () => {
        clearWillChange(targets);
        section.dataset[sectionFlag] = 'true';
        if (typeof options.onComplete === 'function') {
          options.onComplete(section);
        }
        },
      }
    );
  });
};

const animateHero = (selector, options = {}) => {
  const sections = document.querySelectorAll(selector);
  const sectionFlag = `pageHeroAnimated${makeKey(selector)}`;

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset[sectionFlag] === 'true') {
      return;
    }

    const media = options.mediaSelector ? section.querySelector(options.mediaSelector) : null;
    const overlay = options.overlaySelector ? section.querySelector(options.overlaySelector) : null;
    const title = options.titleSelector ? section.querySelector(options.titleSelector) : null;
    const content = options.contentSelector ? section.querySelector(options.contentSelector) : null;

    ScrollTrigger.create({
      trigger: section,
      start: options.start || 'top 80%',
      once: true,
      markers: false,
      onEnter: () => {
        const timeline = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power3.out',
          },
          onStart: () => {
          setWillChange([media, overlay, title, content]);
          },
          onComplete: () => {
            clearWillChange([media, overlay, title, content]);
            section.dataset[sectionFlag] = 'true';
          },
        });

        if (media) {
          timeline.fromTo(
            media,
            { scale: 1.08 },
            {
              scale: 1,
              duration: 1.15,
            }
          );
        }

        if (overlay) {
          timeline.fromTo(
            overlay,
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.95,
            },
            '<0.15'
          );
        }

        if (title) {
          timeline.fromTo(
            title,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.95,
            },
            '<0.05'
          );
        }

        if (content) {
          timeline.fromTo(
            content,
            { y: 28, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.85,
            },
            '<0.08'
          );
        }
      },
    });
  });
};

const showWithoutAnimation = () => {
  const selectors = [
    '.blog-hero__image',
    '.blog-hero__overlay',
    '.blog-hero__copy',
    '.blog-newsletter__card',
    '.blog-sidebar__search',
    '.blog-sidebar__section',
    '.blog-posts__header',
    '.blog-post',
    '.blog-single__header',
    '.blog-single__hero-image',
    '.blog-single__sidebar',
    '.blog-single__toolbar-button',
    '.blog-single__content',
    '.blog-single__cta',
    '.previous-events__modal-banner',
    '.duvidas-hero__title',
    '.duvidas-content__layout',
    '.duvidas-sidebar__group',
    '.duvidas-content__content > *',
    '.duvidas-content__article',
    '.duvidas-banner__card',
    '.esg-hero__image',
    '.esg-hero__overlay',
    '.esg-hero__title',
    '.solutions-hero__image',
    '.solutions-hero__overlay',
    '.solutions-hero__title',
    '.solutions-hero__description',
    '.solutions-entry__badge',
    '.solutions-entry__title',
    '.solutions-entry__text',
    '.solutions-entry__button',
    '.solutions-entry__media--left',
    '.solutions-entry__media--right',
    '.solutions-data__badge',
    '.solutions-data__title',
    '.solutions-data__text',
    '.solutions-data__button',
    '.solutions-data__media',
    '.solutions-technology__badge',
    '.solutions-technology__title',
    '.solutions-technology__text',
    '.solutions-technology__button',
    '.solutions-technology__media',
    '.solutions-partnership__badge',
    '.solutions-partnership__title',
    '.solutions-partnership__text',
    '.solutions-partnership__button',
    '.solutions-partnership__media',
    '.solutions-legacy__badge',
    '.solutions-legacy__title',
    '.solutions-legacy__text',
    '.solutions-legacy__button',
    '.solutions-legacy__media',
    '.esg-badge',
    '.esg-intro__title',
    '.esg-intro__text',
    '.esg-button',
    '.esg-intro__media',
    '.esg-ods__header',
    '.esg-ods__card',
    '.esg-practices__card',
    '.esg-cta__card',
  ];

  gsap.set(selectors.join(', '), {
    clearProps: 'all',
    opacity: 1,
    y: 0,
    scale: 1,
  });
};

const initBlogArchiveAnimations = () => {
  if (!document.querySelector('.blog-hero, .blog-content, .blog-newsletter')) {
    return;
  }

  animateHero('.blog-hero', {
    mediaSelector: '.blog-hero__image',
    overlaySelector: '.blog-hero__overlay',
    titleSelector: '.blog-hero__title',
    contentSelector: '.blog-hero__copy',
    start: 'top 78%',
  });

  revealSection('.blog-newsletter__card', {
    from: { y: 42, opacity: 0, scale: 0.985 },
    to: { duration: 0.95 },
  });

  revealChildren('.blog-content', '.blog-sidebar__search, .blog-sidebar__section, .blog-posts__header', {
    from: { y: 34, opacity: 0 },
    to: { stagger: 0.12, duration: 0.9 },
  });

  revealChildren('.blog-content', '.blog-post', {
    from: { y: 42, opacity: 0, scale: 0.98 },
    to: { stagger: 0.08, duration: 0.95 },
  });
};

const initBlogSingleAnimations = () => {
  if (!document.querySelector('.blog-single')) {
    return;
  }

  revealSection('.blog-single__header', {
    from: { y: 32, opacity: 0 },
    to: { duration: 0.9 },
  });

  animateHero('.blog-single__hero', {
    mediaSelector: '.blog-single__hero-image',
    start: 'top 82%',
  });

  revealChildren('.blog-single__layout', '.blog-single__sidebar, .blog-single__article', {
    from: { y: 38, opacity: 0, scale: 0.99 },
    to: { stagger: 0.12, duration: 0.95 },
  });

  revealChildren('.blog-single__toolbar', '.blog-single__toolbar-button', {
    from: { y: 24, opacity: 0, scale: 0.98 },
    to: { stagger: 0.08, duration: 0.8 },
  });

  revealSection('.blog-single__content', {
    from: { y: 40, opacity: 0 },
    to: { duration: 0.95 },
  });

  revealSection('.blog-single__cta .previous-events__modal-banner', {
    from: { y: 44, opacity: 0, scale: 0.985 },
    to: { duration: 0.95 },
  });
};

const initDuvidasAnimations = () => {
  if (!document.querySelector('.duvidas-hero, .duvidas-content, .duvidas-banner')) {
    return;
  }

  revealSection('.duvidas-hero__title', {
    from: { y: 34, opacity: 0 },
    to: { duration: 0.85 },
    scrollTrigger: { trigger: '.duvidas-hero', start: 'top 82%' },
  });

  revealChildren('.duvidas-content__layout', '.duvidas-sidebar, .duvidas-content__main', {
    from: { y: 42, opacity: 0, scale: 0.99 },
    to: { stagger: 0.12, duration: 0.95 },
  });

  revealChildren('.duvidas-sidebar__groups', '.duvidas-sidebar__group', {
    from: { y: 28, opacity: 0, scale: 0.985 },
    to: { stagger: 0.08, duration: 0.85 },
  });

  revealChildren('.duvidas-content__content', '.duvidas-content__content > *', {
    from: { y: 28, opacity: 0 },
    to: { stagger: 0.08, duration: 0.8 },
  });

  revealSection('.duvidas-content__article', {
    from: { y: 36, opacity: 0 },
    to: { duration: 0.9 },
  });

  revealSection('.duvidas-banner__card', {
    from: { y: 44, opacity: 0, scale: 0.985 },
    to: { duration: 0.95 },
  });
};

const initEsgAnimations = () => {
  if (!document.querySelector('.esg-page')) {
    return;
  }

  animateHero('.esg-hero', {
    mediaSelector: '.esg-hero__image',
    overlaySelector: '.esg-hero__overlay',
    titleSelector: '.esg-hero__title',
    start: 'top 78%',
  });

  revealChildren('.esg-intro__container', '.esg-badge, .esg-intro__title, .esg-intro__text, .esg-button, .esg-intro__media', {
    from: { y: 36, opacity: 0, scale: 0.99 },
    to: { stagger: 0.12, duration: 0.95 },
  });

  revealChildren('.esg-ods__panel', '.esg-ods__header, .esg-ods__card', {
    from: { y: 36, opacity: 0, scale: 0.99 },
    to: { stagger: 0.09, duration: 0.9 },
  });

  revealChildren('.esg-practices__grid', '.esg-practices__card', {
    from: { y: 42, opacity: 0, scale: 0.98 },
    to: { stagger: 0.1, duration: 0.95 },
  });

  revealSection('.esg-cta__card', {
    from: { y: 44, opacity: 0, scale: 0.985 },
    to: { duration: 0.95 },
  });
};

const initSolutionsAnimations = () => {
  if (!document.querySelector('.solutions-hero')) {
    return;
  }

  animateHero('.solutions-hero', {
    mediaSelector: '.solutions-hero__image',
    overlaySelector: '.solutions-hero__overlay',
    titleSelector: '.solutions-hero__title',
    contentSelector: '.solutions-hero__description',
    start: 'top 78%',
  });

  revealChildren(
    '.solutions-entry',
    '.solutions-entry__badge, .solutions-entry__title, .solutions-entry__text, .solutions-entry__button, .solutions-entry__media--left, .solutions-entry__media--right',
    {
      from: { y: 36, opacity: 0, scale: 0.99 },
      to: { stagger: 0.1, duration: 0.95 },
    }
  );

  revealChildren(
    '.solutions-data',
    '.solutions-data__badge, .solutions-data__title, .solutions-data__text, .solutions-data__button, .solutions-data__media',
    {
      from: { y: 38, opacity: 0, scale: 0.99 },
      to: { stagger: 0.1, duration: 0.95 },
    }
  );

  revealChildren(
    '.solutions-technology',
    '.solutions-technology__badge, .solutions-technology__title, .solutions-technology__text, .solutions-technology__button, .solutions-technology__media',
    {
      from: { y: 38, opacity: 0, scale: 0.99 },
      to: { stagger: 0.1, duration: 0.95 },
    }
  );

  revealChildren(
    '.solutions-partnership',
    '.solutions-partnership__badge, .solutions-partnership__title, .solutions-partnership__text, .solutions-partnership__button, .solutions-partnership__media',
    {
      from: { y: 38, opacity: 0, scale: 0.99 },
      to: { stagger: 0.1, duration: 0.95 },
    }
  );

  revealChildren(
    '.solutions-legacy',
    '.solutions-legacy__badge, .solutions-legacy__title, .solutions-legacy__text, .solutions-legacy__button, .solutions-legacy__media',
    {
      from: { y: 38, opacity: 0, scale: 0.99 },
      to: { stagger: 0.1, duration: 0.95 },
    }
  );
};

const initPageAnimations = () => {
  if (!document.body) {
    return;
  }

  const hasTargetPage = Boolean(
    document.querySelector('.blog-hero, .blog-content, .blog-newsletter, .blog-single, .duvidas-page, .duvidas-hero, .duvidas-content, .duvidas-banner, .esg-page, .solutions-hero')
  );

  if (!hasTargetPage || document.body.dataset[ANIMATED_FLAG] === 'true') {
    return;
  }

  document.body.dataset[ANIMATED_FLAG] = 'true';

  if (prefersReducedMotion()) {
    showWithoutAnimation();
    return;
  }

  gsap.defaults({
    duration: 1,
    ease: 'power3.out',
  });

  initBlogArchiveAnimations();
  initBlogSingleAnimations();
  initDuvidasAnimations();
  initEsgAnimations();
  initSolutionsAnimations();

  window.setTimeout(() => {
    ScrollTrigger.refresh();
  }, 250);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPageAnimations);
} else {
  initPageAnimations();
}
